/**
 * Utilitaires de manipulation de dates partagés
 *
 * Centralise les fonctions de date dupliquées à travers le codebase :
 * getMonday, dateKey, generateWeekDays, getToday, getFirstDayOfMonth,
 * addDays, formatDateDB, formatDateLocal, getStartOfWeek, getWeekNumber
 */

/**
 * Formate une date en YYYY-MM-DD avec gestion du fuseau horaire local.
 * Évite le décalage UTC qui peut faire reculer d'un jour.
 * C'est la fonction de base utilisée par dateKey, getToday, getFirstDayOfMonth.
 */
export const formatDateDB = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Retourne le lundi de la semaine contenant la date donnée */
export const getMonday = (d: Date): Date => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

/** Alias de getMonday pour les fichiers qui utilisent ce nom */
export const getStartOfWeek = getMonday;

/** Formate une date en YYYY-MM-DD (timezone locale, cohérent avec getFrenchHolidays) */
export const dateKey = (date: Date): string => formatDateDB(date);

/** Génère un tableau de 7 dates consécutives à partir d'une date de début */
export const generateWeekDays = (start: Date): Date[] =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });

/** Retourne la date du jour au format YYYY-MM-DD */
export const getToday = (): string => formatDateDB(new Date());

/** Retourne le premier jour du mois courant au format YYYY-MM-DD */
export const getFirstDayOfMonth = (): string => {
  const d = new Date();
  return formatDateDB(new Date(d.getFullYear(), d.getMonth(), 1));
};

/** Ajoute n jours à une date et retourne une nouvelle Date */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Formate une date en YYYY-MM-DD en compensant l'offset timezone.
 * Utilisé quand la date vient d'un objet Date potentiellement en UTC.
 */
export const formatDateLocal = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const d = new Date(date.getTime() - (offset * 60 * 1000));
  return d.toISOString().split('T')[0];
};

/** Noms des mois en français — source unique de vérité (était dupliqué 8× dans le codebase) */
export const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

/** Calcule le numéro de semaine ISO d'une date */
export const getWeekNumber = (d: Date): number => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};
